import API from '../api/axios';
import { invalidateCache, fetchWithCache } from '../utils/apiCache';

// =================================================================
// Admin - User/Farmer API Calls
// =================================================================

export const getFarmers = async () => {
    return fetchWithCache('admin_farmers_all', async () => {
        const { data } = await API.get('/api/users/admin/farmers');
        return data;
    }, 10 * 60 * 1000);
};

export const resetUserPassword = async (userId) => {
    const { data } = await API.post(`/api/users/admin/${userId}/reset-password`);
    return data;
};

export const exportFarmersData = async (userIds = []) => {
    const { data } = await API.post('/api/master/export/farmers', { userIds }, {
        responseType: 'blob',
    });
    return data;
};

// =================================================================
// Admin - Password Reset Requests
// =================================================================

export const getResetRequests = async () => {
    return fetchWithCache('admin_reset_requests', async () => {
        const { data } = await API.get('/api/password-reset/admin/requests');
        return data;
    }, 2 * 60 * 1000);
};

export const getResetRequestsCount = async () => {
    const { data } = await API.get('/api/password-reset/admin/requests/count');
    return data;
};

export const resolveResetRequest = async (requestId) => {
    const { data } = await API.put(`/api/password-reset/admin/resolve/${requestId}`);
    invalidateCache(['admin_reset_requests']);
    return data;
};


// Generic factory for creating master data CRUD services
const createMasterDataService = (endpoint) => ({
  getAll: async () => {
    const cacheKey = `master_admin_${endpoint}`;
    return fetchWithCache(cacheKey, async () => {
        const { data } = await API.get(`/api/master/${endpoint}`);
        return data;
    }, 15 * 60 * 1000);
  },
  create: async (itemData) => {
    const { data } = await API.post(`/api/master/${endpoint}`, itemData);
    invalidateCache([`master_admin_${endpoint}`, `master_${endpoint}`, /app_config/]);
    return data;
  },
  update: async (id, itemData) => {
    const { data } = await API.put(`/api/master/${endpoint}/${id}`, itemData);
    invalidateCache([`master_admin_${endpoint}`, `master_${endpoint}`, /app_config/]);
    return data;
  },
  delete: async (id) => {
    await API.delete(`/api/master/${endpoint}/${id}`);
    invalidateCache([`master_admin_${endpoint}`, `master_${endpoint}`, /app_config/]);
  },
});

export const cropMasterApi = createMasterDataService('crops');
export const growthStageMasterApi = createMasterDataService('growth-stages');
export const fertilizerMasterApi = createMasterDataService('fertilizers');
export const pesticideMasterApi = createMasterDataService('pesticides');
export const insecticideMasterApi = createMasterDataService('insecticides');
export const standardDosageApi = createMasterDataService('standard-dosages');

// =================================================================
// Fertilizer Scan & Application
// =================================================================

export const getFertilizerBySlug = async (slug) => {
    const { data } = await API.get(`/api/master/fertilizers/scan/${slug}`);
    return data;
};

export const applyFertilizer = async (applicationData) => {
    const { data } = await API.post('/api/fertilizer-applications', applicationData);
    invalidateCache([/field_details_.*/]);
    return data;
};


// =================================================================
// Admin - Master Data Bulk Upload
// =================================================================

export const downloadTemplate = async (entity) => {
    const { data } = await API.get(`/api/master/template/${entity}`, {
        responseType: 'blob',
    });
    return data;
};

export const downloadData = async (entity) => {
    const { data } = await API.get(`/api/master/data/${entity}`, {
        responseType: 'blob',
    });
    return data;
};

export const uploadMasterData = async (entity, formData) => {
    const { data } = await API.post(`/api/master/bulk-upload/${entity}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    invalidateCache(`master_admin_${entity}`);
    return data;
};

export const getBulkUploadStatus = async (jobId) => {
    const { data } = await API.get(`/api/master/bulk-upload/status/${jobId}`);
    return data;
};

// =================================================================
// Admin - Recommendation API Calls
// =================================================================

export const getAllRecommendations = async (page = 1, limit = 20) => {
    return fetchWithCache(`admin_recommendations_all_page_${page}_limit_${limit}`, async () => {
        const { data } = await API.get(`/api/recommendations/admin/all?page=${page}&limit=${limit}`);
        return data;
    });
};

export const getRecommendationById = async (id) => {
    return fetchWithCache(`recommendation_${id}`, async () => {
        // We can reuse the public endpoint if it supports it, 
        // or add an admin-specific one if needed. 
        // For now, let's assume we fetch all and find, or we might need a new endpoint.
        // Actually, let's check if there's a dedicated GET /api/recommendations/:id
        const { data } = await API.get(`/api/recommendations/${id}`);
        return data;
    });
};

export const overrideRecommendation = async (recommendationId, overrideData) => {
    const { data } = await API.patch(`/api/recommendations/${recommendationId}/override`, overrideData);
    invalidateCache([
        /admin_recommendations_all_page_.*/,
        `recommendation_${recommendationId}`
    ]);
    return data;
};

// =================================================================
// Phase 6 — Admin Monitoring API Calls
// =================================================================

export const getMonitoredFields = async (filters = {}) => {
    const { data } = await API.get('/api/admin/monitoring/fields', { params: filters });
    return data;
};

export const getMonitoredIssues = async (filters = {}) => {
    const { data } = await API.get('/api/admin/monitoring/issues', { params: filters });
    return data;
};

export const reviewFieldIssue = async (issueId, adminNotes) => {
    const { data } = await API.patch(`/api/admin/monitoring/issues/${issueId}/review`, { adminNotes });
    return data;
};

export const getInactiveFields = async (params = {}) => {
    const { data } = await API.get('/api/admin/monitoring/inactive', { params });
    return data;
};